/**
 * NoteAuditSidebar Component
 *
 * Sidebar view for note audit data, showing risks, rules compliance, and billing info
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { cleanCptCodes, CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { useTemplateSwitch } from '@features/note-ai-edit/hooks/useTemplateSwitch';
import { useLazyGetRuleQuery } from '@features/rules/store/api';
import {
  Check as CheckIcon,
  CheckCircle as CompliantIcon,
  Close as CloseIcon,
  CompareArrows as CompareIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, IconButton, Link, Popover, Slide, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks/useModal';

import type { UseNoteAuditResult } from '../../hooks/useNoteAudit';
import { usePayerAwareTemplatesConfig } from '../../hooks/usePayerAwareTemplatesConfig';

import { CPTAnalysisSkeleton, RulesAtRiskSkeleton } from './AuditSkeletons';
import { RulesAtRiskView } from './RulesAtRiskView';

interface NoteAuditSidebarProps {
  /** Note ID for reaudit mutation */
  noteId?: string;
  /** Encounter ID for analytics tracking */
  encounterId?: string;
  /** Current template ID (for regeneration) */
  currentTemplateId?: string;
  /** Current template name (for regeneration) */
  currentTemplateName?: string;
  /** Unified audit hook result */
  audit: UseNoteAuditResult;
  /** Callback to scroll to a specific section */
  onScrollToSection?: (sectionId: string) => void;
  /** Callback to fix issue with AI chat */
  onFixWithAI?: (reason: string) => void;
  /** Callback to create a claim */
  onCreateClaim?: () => void;
  /** Callback when user clicks "Use Suggested Codes" to apply recommended CPT codes */
  onUseSuggestedCodes?: (codes: string[]) => void;
}

export const NoteAuditSidebar: React.FC<NoteAuditSidebarProps> = ({
  noteId,
  encounterId,
  currentTemplateId,
  currentTemplateName,
  audit,
  onScrollToSection,
  onFixWithAI,
  onCreateClaim,
  onUseSuggestedCodes,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { noteAudit } = useEventTracking();
  const rulesContainerRef = useRef<HTMLDivElement>(null);

  // Track dismissed rules count
  const [dismissedRulesCount, setDismissedRulesCount] = useState(0);

  // Banner animation state
  const [showBanner, setShowBanner] = useState(false);
  const bannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bannerShownTimeRef = useRef<number | null>(null);
  
  // Track if payer-aware template banner has been dismissed
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  // CPT picker popover state
  const [cptPickerAnchorEl, setCptPickerAnchorEl] = useState<HTMLElement | null>(null);
  const isCptPickerOpen = Boolean(cptPickerAnchorEl);

  const handleOpenCptPicker = (event: React.MouseEvent<HTMLElement>) => {
    setCptPickerAnchorEl(event.currentTarget);
  };

  const handleCloseCptPicker = () => {
    setCptPickerAnchorEl(null);
  };

  // Get payer-aware template IDs from Statsig dynamic config
  const payerAwareTemplateIds = usePayerAwareTemplatesConfig();

  // Template switching for payer-aware regeneration
  const {
    showConfirmDialog,
    handleTemplateSelect,
    handleConfirmRegenerate,
    handleCancelRegenerate,
    isRegenerating,
    selectedTemplate,
  } = useTemplateSwitch({
    noteId,
    currentTemplateId,
    currentTemplateName,
    encounterId,
  });

  // Extract data from unified audit hook - RTK Query cache is the single source of truth
  const auditData = audit.data;
  const cptAnalysis = audit.cptAnalysis;
  const isLoading = audit.isLoading;
  const isReauditing = audit.isReauditing;

  // Check if current note's template is already a payer-aware template
  const noteTemplateId = audit.auditDetail?.template?.template_id;
  const isAlreadyPayerAwareTemplate =
    noteTemplateId && payerAwareTemplateIds.includes(noteTemplateId);

  // Get the first payer-aware template ID from the list to use for regeneration
  const targetPayerAwareTemplateId = payerAwareTemplateIds[0];

  // Only show banner if compliance is below threshold (50%)
  const COMPLIANCE_THRESHOLD = 50;
  const currentCompliance = audit.auditDetail?.current_revision?.compliance_percentage;
  const isBelowComplianceThreshold =
    currentCompliance !== undefined &&
    currentCompliance !== null &&
    currentCompliance < COMPLIANCE_THRESHOLD;

  // Rule fetching for modal
  const [fetchRule] = useLazyGetRuleQuery();

  const { open: openModal } = useModal();

  const handleViewRule = async (ruleId: string) => {
    // Find the assessment for this rule to get the reason
    const assessment = auditData?.rule_compliance_assessments?.find(a => a.rule_id === ruleId);

    // Fetch the rule details
    const result = await fetchRule(ruleId);

    if (result.data && result.data.rule_type === 'compliance') {
      openModal(MODAL_TYPES.RULE_DETAIL, {
        rule: result.data,
        isAdmin: false,
        assessment: assessment || null,
        noteId: noteId,
      });
    }
  };

  // Tracking callbacks for RuleItem
  const handleRuleClicked = (
    ruleId: string,
    complianceStatus: string,
    hasRelevantSections: boolean,
  ) => {
    noteAudit.ruleClicked({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: ruleId,
      compliance_status: complianceStatus as 'yes' | 'no' | 'warning',
      has_relevant_sections: hasRelevantSections,
    });
  };

  const handleRuleFixClicked = (ruleId: string, complianceStatus: string) => {
    noteAudit.ruleFixClicked({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: ruleId,
      compliance_status: complianceStatus as 'yes' | 'no' | 'warning',
    });
  };

  const handleRuleJumpToSectionClicked = (ruleId: string) => {
    noteAudit.ruleJumpToSectionClicked({
      note_id: noteId,
      encounter_id: encounterId,
      rule_id: ruleId,
    });
  };

  // Handle reanalyze button click
  const handleReanalyze = () => {
    if (!noteId) return;

    // Calculate current issue count
    const currentIssueCount =
      auditData?.rule_compliance_assessments.filter(
        r => r.compliant === 'no' || r.compliant === 'warning',
      ).length || 0;

    // Track run audit clicked with manual source
    noteAudit.runClicked({
      note_id: noteId,
      encounter_id: encounterId,
      current_issue_count: currentIssueCount,
      source: 'manual',
    });

    // Trigger reaudit using the unified hook
    audit.triggerReaudit();
  };

  // Track CPT displayed when CPT section is shown (with new target_cpt_codes fields)
  const cptDisplayedTrackedRef = useRef(false);
  useEffect(() => {
    const targetCodes = cptAnalysis?.targetCptCodes;
    const hasTargetCodes = targetCodes && targetCodes.length > 0;
    if (hasTargetCodes && !cptDisplayedTrackedRef.current) {
      cptDisplayedTrackedRef.current = true;
      
      // Check if recommended codes differ from target codes
      const targetCodesSet = new Set(targetCodes.filter(c => c && c.trim()));
      const recommendedCodesSet = new Set((cptAnalysis.recommendedCptCodes ?? []).filter(c => c && c.trim()));
      const hasDifferentSuggestion = Boolean(
        cptAnalysis.recommendedCptCodes && 
        cptAnalysis.recommendedCptCodes.length > 0 &&
        (targetCodesSet.size !== recommendedCodesSet.size ||
         [...targetCodesSet].some(c => !recommendedCodesSet.has(c)))
      );

      // Track the new cptDisplayed event with full context
      noteAudit.cptDisplayed({
        note_id: noteId,
        encounter_id: encounterId,
        target_cpt_source: cptAnalysis.targetCptSource ?? undefined,
        target_codes_count: targetCodes.filter(c => c?.trim()).length,
        cpt_match: cptAnalysis.cptMatch,
        has_different_suggestion: hasDifferentSuggestion,
      });

      // Also fire legacy cptViewed for backward compatibility
      noteAudit.cptViewed({
        note_id: noteId,
        encounter_id: encounterId,
        cpt_match: cptAnalysis.cptMatch,
        provider_codes_count: cptAnalysis.providerCptCodes?.filter(c => c?.trim()).length ?? 0,
        recommended_codes_count:
          cptAnalysis.recommendedCptCodes?.filter(c => c?.trim()).length ?? 0,
      });
    }
  }, [cptAnalysis, noteId, encounterId, noteAudit]);

  // Handle mouse enter for banner animation
  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current);
    }

    // Set new timeout to show banner after 3 seconds
    bannerTimeoutRef.current = setTimeout(() => {
      setShowBanner(true);
      bannerShownTimeRef.current = Date.now();
      
      // Track banner shown
      noteAudit.payerAwareBannerShown({
        note_id: noteId,
        encounter_id: encounterId,
        current_template_id: noteTemplateId,
        current_compliance: currentCompliance ?? undefined,
        target_template_id: targetPayerAwareTemplateId,
      });
    }, 3000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current);
      }
    };
  }, []);

  // Reset banner when visibility conditions change
  useEffect(() => {
    if (!isBelowComplianceThreshold || isAlreadyPayerAwareTemplate || isBannerDismissed) {
      setShowBanner(false);
      if (bannerTimeoutRef.current) {
        clearTimeout(bannerTimeoutRef.current);
        bannerTimeoutRef.current = null;
      }
    }
  }, [isBelowComplianceThreshold, isAlreadyPayerAwareTemplate, isBannerDismissed]);

  // Handle banner dismiss
  const handleBannerDismiss = () => {
    const timeVisibleMs = bannerShownTimeRef.current 
      ? Date.now() - bannerShownTimeRef.current 
      : undefined;
    
    // Track banner dismissal
    noteAudit.payerAwareBannerDismissed({
      note_id: noteId,
      encounter_id: encounterId,
      current_template_id: noteTemplateId,
      current_compliance: currentCompliance ?? undefined,
      time_visible_ms: timeVisibleMs,
    });
    
    setIsBannerDismissed(true);
    setShowBanner(false);
    bannerShownTimeRef.current = null;
  };

  // Derive loading/polling state
  const isPolling = audit.state === 'polling_initial';
  // Don't show full loading state during reaudit - we show progressive skeletons instead
  const showLoading = (isLoading || isPolling) && !isReauditing;

  // Check if any data has arrived via streaming
  const hasAnyData =
    cptAnalysis?.cptAnalysisPerformed ||
    (auditData?.rule_compliance_assessments && auditData.rule_compliance_assessments.length > 0);

  if (showLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: theme.palette.background.paper,
          position: 'relative',
        }}
      >
        {/* Floating "Running Audit" indicator - only show when no data has arrived yet */}
        {!hasAnyData && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 4,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              minWidth: 200,
            }}
          >
            <CircularProgress size={40} thickness={4} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'textColors.primaryText' }}>
              Running Audit...
            </Typography>
          </Box>
        )}

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Box sx={{ p: 2 }}>
            <CPTAnalysisSkeleton />
          </Box>
          <RulesAtRiskSkeleton cardCount={6} />
        </Box>
      </Box>
    );
  }

  // Only show "No audit data" when NOT reauditing, NOT loading, and no data exists
  // During reaudit or refetch, we continue to render (skeletons will show for missing sections)
  if (!auditData && !isReauditing && !isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Box sx={{ p: 2 }}>
            <CPTAnalysisSkeleton />
          </Box>
          <RulesAtRiskSkeleton cardCount={6} />
        </Box>
        {audit.state === 'error' && audit.error && (
          <Box sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.error.light }}>
            <Typography variant="caption" color="error">
              {audit.error}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: theme.palette.background.paper,
        position: 'relative',
      }}
    >
      {/* Floating "Running Audit" indicator - only show when no data has arrived yet */}
      {(isReauditing || isLoading) && !hasAnyData && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 4,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            minWidth: 200,
          }}
        >
          <CircularProgress size={40} thickness={4} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'textColors.primaryText' }}>
            Running Audit...
          </Typography>
        </Box>
      )}

      {/* Scrollable Content Section */}
      <Box ref={rulesContainerRef} sx={{ flex: 1, overflow: 'auto' }}>
        {/* CPT Analysis Section - Padding for this section only */}
        <Box sx={{ p: 2 }}>
          {/* Show skeleton when reauditing/loading and CPT data hasn't arrived yet */}
          {(isReauditing || isLoading) && !cptAnalysis?.targetCptCodes && (
            <CPTAnalysisSkeleton />
          )}
          {/* Show CPT analysis when target_cpt_codes exist (regardless of cpt_analysis_performed) */}
          {cptAnalysis?.targetCptCodes && cptAnalysis.targetCptCodes.length > 0 &&
            (() => {
              const cptSource = cptAnalysis.targetCptSource;
              const isMatch = cptAnalysis.cptMatch;
              const providerCodes = cptAnalysis.providerCptCodes;
              const recommendedCodes = cptAnalysis.recommendedCptCodes;
              const targetCodes = cptAnalysis.targetCptCodes;

              // Format codes as comma-separated strings
              const providerCodesStr = providerCodes
                ?.filter(code => code && code.trim())
                .join(', ');
              const recommendedCodesStr = recommendedCodes
                ?.filter(code => code && code.trim())
                .join(', ');

              // Get billing support data for additional context (may be null)
              const billingSupport = auditData?.billing_support;

              // Check if recommended codes differ from target codes
              const targetCodesSet = new Set(targetCodes.filter(c => c && c.trim()));
              const recommendedCodesSet = new Set((recommendedCodes ?? []).filter(c => c && c.trim()));
              const hasDifferentSuggestion = Boolean(
                recommendedCodes && 
                recommendedCodes.length > 0 &&
                (targetCodesSet.size !== recommendedCodesSet.size ||
                 [...targetCodesSet].some(c => !recommendedCodesSet.has(c)))
              );

              // Determine scenario styling
              let cardColor: string;
              let statusLabel: string;
              let StatusIcon: typeof CompliantIcon;
              let showApplyButton = false;

              if (cptSource === 'user') {
                if (isMatch) {
                  // Scenario 1: User codes, AI agrees ✅
                  cardColor = theme.palette.success.main;
                  statusLabel = 'Codes Supported';
                  StatusIcon = CompliantIcon;
                } else {
                  // Scenario 2: User codes, AI suggests different 💡
                  cardColor = theme.palette.warning.main;
                  statusLabel = 'Review Suggested';
                  StatusIcon = WarningIcon;
                  showApplyButton = true;
                }
              } else {
                // Scenario 3: AI suggested codes for audit 🤖
                cardColor = theme.palette.info.main;
                statusLabel = 'Suggested Codes';
                StatusIcon = CompareIcon;
                showApplyButton = hasDifferentSuggestion;
              }

              // Show "Apply" button if we have recommendations different from current
              const showUseSuggested =
                showApplyButton &&
                recommendedCodes &&
                recommendedCodes.filter(c => c && c.trim()).length > 0 &&
                onUseSuggestedCodes;

              return (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: `${cardColor}08`,
                    border: `1px solid ${cardColor}25`,
                  }}
                >
                  {/* Header row: Status badge */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: cardColor,
                      mb: 1.25,
                    }}
                  >
                    <StatusIcon sx={{ fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, letterSpacing: '0.02em' }}
                    >
                      {statusLabel}
                    </Typography>
                  </Box>

                  {/* Primary Section: What the audit was based on */}
                  <Box sx={{ mb: hasDifferentSuggestion ? 1.5 : 0 }}>
                    {/* Label for audit basis */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'textColors.secondaryText',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        mb: 0.5,
                      }}
                    >
                      {cptSource === 'user' ? 'Audit based on your codes' : 'Based on your documentation'}
                    </Typography>

                    {/* Target CPT Codes - The codes this audit was based on */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        alignItems: 'center',
                      }}
                    >
                      {targetCodes.filter(c => c && c.trim()).map((code, idx) => (
                        <Chip
                          key={`target-${idx}`}
                          label={code}
                          size="small"
                          clickable={cptSource === 'ai' && !!onUseSuggestedCodes}
                          onClick={
                            cptSource === 'ai' && onUseSuggestedCodes
                              ? (e) => {
                                  // Track picker opened via chip click
                                  noteAudit.cptPickerOpened({
                                    note_id: noteId,
                                    encounter_id: encounterId,
                                    trigger: 'chip_click',
                                    target_cpt_source: cptSource ?? undefined,
                                    current_codes: targetCodes.filter(c => c && c.trim()),
                                  });
                                  handleOpenCptPicker(e);
                                }
                              : undefined
                          }
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            bgcolor: `${cardColor}20`,
                            color: cardColor,
                            border: `2px solid ${cardColor}`,
                            height: '28px',
                            '& .MuiChip-label': {
                              px: 1.5,
                            },
                            // Clickable styles for AI-suggested codes
                            ...(cptSource === 'ai' && onUseSuggestedCodes && {
                              cursor: 'pointer',
                              transition: 'all 0.15s ease-in-out',
                              '&:hover': {
                                bgcolor: `${cardColor}35`,
                                transform: 'scale(1.02)',
                                boxShadow: `0 2px 8px ${cardColor}40`,
                              },
                              '&:active': {
                                transform: 'scale(0.98)',
                              },
                            }),
                          }}
                        />
                      ))}

                      {/* Action buttons for suggested codes */}
                      {cptSource === 'ai' && onUseSuggestedCodes && (
                        <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5 }}>
                          {/* Check/Accept button */}
                          <Tooltip title="Use this code" arrow>
                            <IconButton
                              size="small"
                              disabled={isReauditing}
                              onClick={() => {
                                const codes = cleanCptCodes(
                                  targetCodes.filter(c => c && c.trim()),
                                );
                                // Track CPT approved (user accepted the suggestion)
                                noteAudit.cptApproved({
                                  note_id: noteId,
                                  encounter_id: encounterId,
                                  codes_approved: codes,
                                  target_cpt_source: cptSource ?? undefined,
                                });
                                // Also track legacy event for backward compatibility
                                noteAudit.suggestedCodesApplied({
                                  note_id: noteId,
                                  encounter_id: encounterId,
                                  codes_applied: codes,
                                  source: 'audit_sidebar',
                                });
                                onUseSuggestedCodes(codes);
                                setTimeout(() => handleReanalyze(), 500);
                              }}
                              sx={{
                                width: 24,
                                height: 24,
                                bgcolor: `${cardColor}15`,
                                color: cardColor,
                                '&:hover': {
                                  bgcolor: `${cardColor}30`,
                                },
                              }}
                            >
                              <CheckIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          {/* Edit/Change button */}
                          <Tooltip title="Change code" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                // Track picker opened via edit button
                                noteAudit.cptPickerOpened({
                                  note_id: noteId,
                                  encounter_id: encounterId,
                                  trigger: 'edit_button',
                                  target_cpt_source: cptSource ?? undefined,
                                  current_codes: targetCodes.filter(c => c && c.trim()),
                                });
                                handleOpenCptPicker(e);
                              }}
                              sx={{
                                width: 24,
                                height: 24,
                                bgcolor: theme.palette.grey[200],
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                  bgcolor: theme.palette.grey[300],
                                },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>

                    {/* Helper text for suggested codes */}
                    {cptSource === 'ai' && onUseSuggestedCodes && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: 'textColors.secondaryText',
                          fontSize: '0.7rem',
                          mt: 0.75,
                        }}
                      >
                        We audited your note against these codes. Click to approve or change.
                      </Typography>
                    )}
                  </Box>

                  {/* Secondary Section: AI Suggestion (only if different from target) */}
                  {hasDifferentSuggestion && (
                    <Box
                      sx={{
                        pt: 1.25,
                        borderTop: `1px dashed ${theme.palette.grey[300]}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'textColors.secondaryText',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          AI recommends instead
                        </Typography>

                        {/* Apply button */}
                        {showUseSuggested && (
                          <Button
                            variant="text"
                            size="small"
                            disabled={isReauditing}
                            onClick={e => {
                              e.stopPropagation();
                              const codes = cleanCptCodes(
                                cptAnalysis.recommendedCptCodes?.filter(c => c && c.trim()) || [],
                              );
                              // Track CPT suggested codes applied
                              noteAudit.suggestedCodesApplied({
                                note_id: noteId,
                                encounter_id: encounterId,
                                codes_applied: codes,
                                source: 'audit_sidebar',
                              });
                              onUseSuggestedCodes(codes);
                              setTimeout(() => handleReanalyze(), 500);
                            }}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              py: 0.25,
                              px: 1,
                              minWidth: 'auto',
                              color: 'primary.main',
                              '&:hover': { bgcolor: 'primary.main', color: 'white' },
                            }}
                          >
                            {isReauditing ? '...' : 'Use These'}
                          </Button>
                        )}
                      </Box>

                      {/* Recommended codes */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.75,
                          alignItems: 'center',
                        }}
                      >
                        {recommendedCodes?.filter(c => c && c.trim()).map((code, idx) => (
                          <Chip
                            key={`recommended-${idx}`}
                            label={code}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              borderColor: theme.palette.primary.main,
                              color: theme.palette.primary.main,
                              height: '24px',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Concern - compact, only if exists */}
                  {billingSupport?.concerns?.[0] && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 1,
                        color: 'warning.dark',
                        lineHeight: 1.3,
                        fontSize: '0.7rem',
                      }}
                    >
                      ⚠ {billingSupport.concerns[0]}
                    </Typography>
                  )}
                </Box>
              );
            })()}
        </Box>

        {/* Rules View - Show skeleton during reaudit/loading until rules arrive */}
        {(isReauditing || isLoading) && !auditData?.rule_compliance_assessments?.length ? (
          <RulesAtRiskSkeleton cardCount={6} />
        ) : auditData?.rule_compliance_assessments ? (
          <RulesAtRiskView
            rules={auditData.rule_compliance_assessments}
            onViewRule={handleViewRule}
            onFixWithAI={onFixWithAI ?? (() => {})}
            onScrollToSection={onScrollToSection}
            onRuleClicked={handleRuleClicked}
            onRuleFixClicked={handleRuleFixClicked}
            onRuleInfoViewed={handleRuleJumpToSectionClicked}
            onDismissedRulesChange={setDismissedRulesCount}
            noteId={noteId}
            encounterId={encounterId}
          />
        ) : null}
      </Box>

      {/* Bottom Section - Footer and Re-analyze Button */}
      <Box
        sx={{
          borderTop: `1px solid ${theme.palette.grey[200]}`,
          bgcolor: theme.palette.grey[50],
        }}
      >
        {/* Payer-Aware Template Banner */}
        {isBelowComplianceThreshold && !isAlreadyPayerAwareTemplate && !isBannerDismissed && (
            <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
              <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: '8px',
                    bgcolor: '#ffffff',
                    borderLeft: `12px solid ${theme.palette.info.main}`,
                    boxShadow: '0px 3px 6px rgba(0,0,0,0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: '0px 4px 10px rgba(0,0,0,0.18)',
                    },
                  }}
                >
                  {/* Dismiss Button */}
                  <IconButton
                    size="small"
                    onClick={handleBannerDismiss}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'text.primary',
                      },
                    }}
                    aria-label="Dismiss banner"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#181E3B',
                      fontWeight: 600,
                      lineHeight: 1.5,
                      fontSize: '0.95rem',
                      pr: 3, // Add padding to prevent text overlap with dismiss button
                    }}
                  >
                    Want to try a more compliant, payer-aware template?
                  </Typography>
                <Button
                  variant="contained"
                  size="medium"
                  fullWidth
                  disabled={isRegenerating || !targetPayerAwareTemplateId}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    py: 1.25,
                  }}
                  onClick={() => {
                    if (!targetPayerAwareTemplateId) return;
                    
                    // Track regenerate click
                    noteAudit.payerAwareRegenerateClicked({
                      note_id: noteId,
                      encounter_id: encounterId,
                      current_template_id: noteTemplateId,
                      target_template_id: targetPayerAwareTemplateId,
                      current_compliance: currentCompliance ?? undefined,
                    });
                    
                    // Trigger template switch with payer-aware template
                    handleTemplateSelect({
                      template_id: targetPayerAwareTemplateId,
                      display_name: 'Payer-optimized template',
                      type: 'public',
                    });
                  }}
                >
                  {isRegenerating ? 'Regenerating...' : 'Regenerate Note'}
                </Button>
                </Box>
              </Box>
            </Slide>
          )}

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            pb: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <CompliantIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{ flex: 1, color: 'textColors.primaryText', lineHeight: 1.5 }}
          >
            {dismissedRulesCount > 0 ? (
              <>
                You've marked {dismissedRulesCount} rule{dismissedRulesCount === 1 ? '' : 's'} as
                done. Press{' '}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  Run Audit
                </Typography>{' '}
                to refresh your results.
              </>
            ) : (
              <>
                Note automatically audited against{' '}
                <Link
                  component="button"
                  sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => {
                    noteAudit.rulesLinkClicked({ note_id: noteId });
                    navigate('/jotaudit?tab=rules');
                  }}
                >
                  your clinic's rules
                </Link>
              </>
            )}
          </Typography>
        </Box>

        {/* Run Audit Button */}
        <Box sx={{ px: 2, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleReanalyze}
            disabled={!noteId || isReauditing}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              py: 1.5,
            }}
          >
            {isReauditing ? 'Running Audit...' : 'Run Audit'}
          </Button>
        </Box>
      </Box>

      {/* Template Regeneration Confirmation Dialog */}
      <ConfirmDialog
        open={showConfirmDialog}
        variant="info"
        title="Switch to Payer-Optimized Template?"
        message={
          <>
            <Typography variant="body2" gutterBottom>
              This will regenerate your note using a template optimized for payer compliance.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Current template: <strong>{currentTemplateName || 'Unknown'}</strong>
              <br />
              New template: <strong>Payer-optimized template</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: 'warning.main' }}>
              Note: This will create a new version of your note.
            </Typography>
          </>
        }
        onConfirm={handleConfirmRegenerate}
        onClose={handleCancelRegenerate}
        cancelText="Cancel"
        confirmText="Regenerate Note"
        loading={isRegenerating}
      />

      {/* CPT Codes Picker Popover */}
      <Popover
        open={isCptPickerOpen}
        anchorEl={cptPickerAnchorEl}
        onClose={handleCloseCptPicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxWidth: '90vw',
              p: 2,
            },
          },
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">CPT Codes</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select up to two CPT codes for this encounter.
              </Typography>
            </Box>
            <IconButton onClick={handleCloseCptPicker} size="small" sx={{ ml: 2 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <CPTCodePicker
            value={audit.cptAnalysis?.targetCptCodes?.filter(c => c && c.trim()) || []}
            onChange={(codes) => {
              const cleanedCodes = cleanCptCodes(codes);
              const previousCodes = audit.cptAnalysis?.targetCptCodes?.filter(c => c && c.trim()) || [];
              
              // Track CPT changed event (new detailed tracking)
              noteAudit.cptChanged({
                note_id: noteId,
                encounter_id: encounterId,
                previous_codes: previousCodes,
                new_codes: cleanedCodes,
                target_cpt_source: audit.cptAnalysis?.targetCptSource ?? undefined,
              });

              // Also track legacy event for backward compatibility
              noteAudit.suggestedCodesApplied({
                note_id: noteId,
                encounter_id: encounterId,
                codes_applied: cleanedCodes,
                source: 'audit_sidebar',
              });

              // Apply codes
              if (onUseSuggestedCodes) {
                onUseSuggestedCodes(cleanedCodes);
              }

              // Close picker
              handleCloseCptPicker();

              // Trigger reaudit
              setTimeout(() => handleReanalyze(), 500);
            }}
            showHeader={false}
            suggestedCodes={audit.cptAnalysis?.recommendedCptCodes ?? undefined}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default NoteAuditSidebar;
